import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './store/AuthContext';
import Layout from './components/common/Layout';
import Login from './pages/auth/Login';
import Dashboard from './pages/dashboard/Dashboard';
import UnitList from './pages/units/UnitList';
import OwnerList from './pages/owners/OwnerList';
import InvoiceList from './pages/billing/InvoiceList';
import PaymentList from './pages/billing/PaymentList';
import FeeTypeList from './pages/billing/FeeTypeList';
import ChartOfAccounts from './pages/accounting/ChartOfAccounts';
import JournalEntryList from './pages/accounting/JournalEntryList';
import TrialBalance from './pages/accounting/TrialBalance';
import MaintenanceRequestList from './pages/maintenance/MaintenanceRequestList';
import AmenityList from './pages/amenities/AmenityList';
import AmenityBookingList from './pages/amenities/AmenityBookingList';
import BackupRestore from './pages/system/BackupRestore';
import FundTypeList from './pages/system/FundTypeList';
import AgingReport from './pages/accounting/AgingReport';
import ProfitLoss from './pages/accounting/ProfitLoss';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
    background: { default: '#f5f5f5' },
  },
});

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="units" element={<UnitList />} />
              <Route path="owners" element={<OwnerList />} />
              <Route path="billing/invoices" element={<InvoiceList />} />
              <Route path="billing/payments" element={<PaymentList />} />
              <Route path="billing/fee-types" element={<FeeTypeList />} />
              <Route path="accounting/chart-of-accounts" element={<ChartOfAccounts />} />
              <Route path="accounting/journal-entries" element={<JournalEntryList />} />
              <Route path="accounting/trial-balance" element={<TrialBalance />} />
              <Route path="maintenance" element={<MaintenanceRequestList />} />
              <Route path="amenities" element={<AmenityList />} />
              <Route path="amenities/bookings" element={<AmenityBookingList />} />
              <Route path="system/backups" element={<BackupRestore />} />
              <Route path="system/fund-types" element={<FundTypeList />} />
              <Route path="accounting/aging" element={<AgingReport />} />
              <Route path="accounting/profit-loss" element={<ProfitLoss />} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
